import { Alert, Button, Drawer, message, Popconfirm, Select, Table, Tag, Typography } from 'antd';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useRef, useState } from 'react';

import { formatMNT, queryErrorMessage } from '../CashTransactions/types';
import type { FeeOption, IssuePreview, IssueTarget } from './types';
import { newIdempotencyKey, TARGET_LABEL } from './types';

const { Text } = Typography;

interface Option {
  id: number;
  name: string;
}

// Issue (CONTEXT.md): pick a Fee and who gets it, preview who is invoiced and
// who skipped, then confirm. Nothing is created before the confirm.
export default function IssueDrawer({
  fees,
  onClose,
  onDone,
}: {
  fees: FeeOption[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { runQuery } = useScreen();

  const [feeId, setFeeId] = useState<number>();
  const [targetType, setTargetType] = useState<IssueTarget>('CLASS');
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [classYears, setClassYears] = useState<Option[]>([]);
  const [classes, setClasses] = useState<Option[]>([]);
  const [students, setStudents] = useState<{ id: number; label: string }[]>([]);
  const [preview, setPreview] = useState<IssuePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmKey = useRef<string | null>(null);

  useEffect(() => {
    runQuery<{ class_years: Option[]; classes: Option[] }>('invoice_target_options')
      .then(([row]) => {
        setClassYears(row?.class_years ?? []);
        setClasses(row?.classes ?? []);
      })
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const fee = fees.find((f) => f.id === feeId);

  const reset = () => {
    setPreview(null);
    setError(null);
  };

  const searchStudents = (search: string) => {
    if (!search.trim()) return;
    runQuery<{ id: number; full_name: string; code: string }>('invoice_student_search', { search })
      .then((rows) =>
        setStudents((prev) => {
          const chosen = prev.filter((s) => targetIds.includes(s.id));
          const found = rows
            .filter((r) => !targetIds.includes(r.id))
            .map((r) => ({ id: r.id, label: `${r.full_name} (${r.code})` }));
          return [...chosen, ...found];
        }),
      )
      .catch((e) => message.error(queryErrorMessage(e)));
  };

  const handlePreview = async () => {
    if (!feeId) return;
    setLoading(true);
    setError(null);
    try {
      if (preview) await runQuery('invoice_issue_cancel', { issueId: preview.id }).catch(() => {});
      const [row] = await runQuery<IssuePreview>('invoice_issue_preview', {
        feeId,
        targetType,
        targetIds: targetType === 'SCHOOL' ? [] : targetIds,
      });
      setPreview(row ?? null);
      confirmKey.current = newIdempotencyKey();
    } catch (e) {
      setError(queryErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setLoading(true);
    setError(null);
    try {
      const [row] = await runQuery<{ invoiced: number; skipped: number }>('invoice_issue_confirm', {
        issueId: preview.id,
        idempotencyKey: confirmKey.current,
      });
      message.success(`${row?.invoiced ?? 0} нэхэмжлэх үүслээ, ${row?.skipped ?? 0} алгассан.`);
      onDone();
    } catch (e) {
      setError(queryErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (preview) runQuery('invoice_issue_cancel', { issueId: preview.id }).catch(() => {});
    onClose();
  };

  const targetOptions =
    targetType === 'CLASS_YEAR'
      ? classYears.map((y) => ({ value: y.id, label: y.name }))
      : targetType === 'CLASS'
        ? classes.map((c) => ({ value: c.id, label: c.name }))
        : students.map((s) => ({ value: s.id, label: s.label }));

  return (
    <Drawer title="Нэхэмжлэх үүсгэх" open onClose={handleClose} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Select
          placeholder="Төлбөр сонгох"
          value={feeId}
          onChange={(value) => {
            setFeeId(value);
            reset();
          }}
          showSearch
          optionFilterProp="label"
          options={fees.map((f) => ({
            value: f.id,
            label: `${f.name} — ${formatMNT(f.amount)}${f.season_name ? ` (${f.season_name})` : ''}`,
          }))}
        />

        <Select
          value={targetType}
          onChange={(value) => {
            setTargetType(value);
            setTargetIds([]);
            reset();
          }}
          options={(Object.keys(TARGET_LABEL) as IssueTarget[]).map((t) => ({
            value: t,
            label: TARGET_LABEL[t],
          }))}
        />

        {targetType !== 'SCHOOL' && (
          <Select
            mode="multiple"
            placeholder={targetType === 'STUDENT' ? 'Нэр эсвэл кодоор хайх' : 'Сонгох'}
            value={targetIds}
            onChange={(value) => {
              setTargetIds(value);
              reset();
            }}
            showSearch
            filterOption={targetType === 'STUDENT' ? false : undefined}
            optionFilterProp="label"
            onSearch={targetType === 'STUDENT' ? searchStudents : undefined}
            options={targetOptions}
          />
        )}

        <Button
          onClick={handlePreview}
          loading={loading && !preview}
          disabled={!feeId || (targetType !== 'SCHOOL' && targetIds.length === 0)}
        >
          Урьдчилан харах
        </Button>

        {preview && fee && (
          <>
            <Alert
              type="info"
              showIcon
              message={`${preview.invoice_count} сурагч нэхэмжилнэ (${formatMNT(fee.amount * preview.invoice_count)}), ${preview.skip_count} алгасна.`}
              description="Алгассан сурагчид энэ төлбөрийн нэхэмжлэхтэй байна — давхар нэхэмжлэхгүй."
            />
            <Table
              size="small"
              rowKey="id"
              dataSource={preview.items}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
              columns={[
                { title: 'Сурагч', dataIndex: 'student_name' },
                { title: 'Код', dataIndex: 'student_code' },
                { title: 'Бүлэг', dataIndex: 'class_name' },
                {
                  title: '',
                  dataIndex: 'status',
                  render: (status: string) =>
                    status === 'SKIP' ? <Tag>Алгасна</Tag> : <Tag color="blue">Нэхэмжилнэ</Tag>,
                },
              ]}
            />
          </>
        )}

        {error && <Alert type="error" showIcon message={error} />}

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button onClick={handleClose} disabled={loading}>
            Болих
          </Button>
          <Popconfirm
            title="Нэхэмжлэх үүсгэх үү?"
            description={
              preview && fee ? (
                <Text>
                  {preview.invoice_count} нэхэмжлэх үүсч, асран халамжлагчдад мэдэгдэл очно.
                </Text>
              ) : null
            }
            okText="Үүсгэх"
            cancelText="Болих"
            onConfirm={handleConfirm}
            disabled={!preview || preview.invoice_count === 0}
          >
            <Button
              type="primary"
              loading={loading && !!preview}
              disabled={!preview || preview.invoice_count === 0}
            >
              Үүсгэх
            </Button>
          </Popconfirm>
        </div>
      </div>
    </Drawer>
  );
}
